/**
 * Simple wrapper to call the Python backend email endpoint.
 * Expects a FastAPI route: POST /api/incidents/email
 *
 * Env:
 *   VITE_INCIDENT_API (optional) e.g. http://localhost:8001/api
 */
export interface IncidentEmailPayload {
  incident_id: string;
  to: string[];        // recipient list
  subject: string;
  html: string;        // populated template
  text?: string;       // plain text fallback
}

const mode = import.meta.env.MODE;
let baseurl: string;

if (mode === 'production') {
  baseurl = 'https://askawatch.cbre.com';
  console.log('FetchIncidents: Running in PRODUCTION mode');
} else if (mode === 'staging') {
  baseurl = 'https://askawatch-test.cbre.com';
  console.log('FetchIncidents: Running in non prod mode');
} else {
  baseurl = 'http://localhost:8000';
  console.log('FetchIncidents: Running in DEVELOPMENT mode');
}

const API_BASE =
  (import.meta as any).env.VITE_INCIDENT_API || `${baseurl}/api`;

export async function sendIncidentEmail(payload: IncidentEmailPayload): Promise<void> {
  const resp = await fetch(`${API_BASE}/incidents/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!resp.ok) {
    const msg = await resp.text().catch(() => '');
    throw new Error(`Email send failed (${resp.status}): ${msg || resp.statusText}`);
  }
}

export async function saveEmailSubject(
  incidentNumber: string,
  subject: string
): Promise<{
  success: boolean;
  incident_number: string;
  updated?: {
    email_subject: string;
    updated_at: string;
  };
  error?: string;
}> {
  try {
    const resp = await fetch(`${API_BASE}/incidents/update_email_subject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        incident_number: incidentNumber,
        email_subject: subject
      })
    });

    if (!resp.ok) {
      const msg = await resp.text().catch(() => '');
      throw new Error(`Save subject failed (${resp.status}): ${msg || resp.statusText}`);
    }

    const data = await resp.json();
    return {
      success: true,
      incident_number: incidentNumber,
      updated: data.updated
    };
  } catch (error) {
    console.error('Save email subject failed:', error);
    return {
      success: false,
      incident_number: incidentNumber,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

export async function rephrasEmailSubject(
  originalSubject: string,
  incidentNumber: string,
  severity?: string
): Promise<{
  success: boolean;
  rephrased_subject?: string;
  error?: string;
}> {
  try {
    // Step 1: Get LLM rephrased version
    const resp = await fetch(`${API_BASE}/llm/generate_email_subject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subject: originalSubject,
        incident_number: incidentNumber,
        severity: severity
      })
    });

    if (!resp.ok) {
      const msg = await resp.text().catch(() => '');
      throw new Error(`Rephrase failed (${resp.status}): ${msg || resp.statusText}`);
    }

    const data = await resp.json();
    const rephrasedSubject = data.rephrased_subject || originalSubject;

    // ✅ Step 2: Save rephrased subject to database
    await saveEmailSubject(incidentNumber, rephrasedSubject);

    return {
      success: true,
      rephrased_subject: rephrasedSubject
    };
  } catch (error) {
    console.error('Rephrase subject failed:', error);
    
    // ✅ FALLBACK: Simple transformation if backend unavailable
    const dummy = originalSubject
      .replace(/Update/i, 'Status Update')
      .replace(/Circumvented/i, 'Resolved & Circumvented');
    
    // Ensure it starts with incident number
    const withId = dummy.includes(incidentNumber)
      ? dummy
      : `${incidentNumber} - ${dummy}`;
    
    // Truncate if too long
    const truncated = withId.length > 150 
      ? withId.substring(0, 147) + '...'
      : withId;
    
    return {
        success: true,
        rephrased_subject: truncated
    };
  }
}